import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { StatusChip, type StatusTone } from '@/components/ui/status-chip';
import { bedLabel } from '@/components/IPD/admissionStatus';
import { Plus, ChevronDown, ChevronUp, Pencil, Bed as BedIcon, AlertTriangle, ArrowLeft, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getWards, createWard, updateWard,
  getBeds, createBed, updateBed, setWardDailyCharges,
  type Ward, type Bed, type DailyChargeType,
} from '@/lib/ipdApi';
import { usePageTitle } from "@/hooks/usePageTitle";

const WARD_TYPES = ['general','private','semi_private','icu','maternity','pediatric','other'] as const;
const BED_STATUSES = ['available','maintenance','reserved'] as const;

// ─── Ward form ────────────────────────────────────────────────────────────────

interface WardForm { name: string; ward_type: string; default_rent_per_day: string; notes: string; is_active: boolean; }
const emptyWard = (): WardForm => ({ name: '', ward_type: 'general', default_rent_per_day: '0', notes: '', is_active: true });

// ─── Daily ward charges ─────────────────────────────────────────────────────────
// Charged per night of the stay like the bed; locked for patients already in the ward

interface DailyRow { name: string; charge_type: DailyChargeType; amount: string }
const DAILY_TYPES: { value: DailyChargeType; label: string }[] = [
  { value: 'nursing', label: 'Nursing' },
  { value: 'consultation', label: 'Doctor / RMO' },
  { value: 'diet', label: 'Diet' },
  { value: 'other', label: 'Other' },
];
const dailyRows = (w: Ward): DailyRow[] =>
  (w.daily_charges ?? []).map(d => ({ name: d.name, charge_type: d.charge_type, amount: String(d.amount) }));

function DailyChargesEditor({ rows, onChange }: { rows: DailyRow[]; onChange: (rows: DailyRow[]) => void }) {
  const update = (i: number, patch: Partial<DailyRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      <div>
        <span className="block text-xs font-medium">Daily charges</span>
        <p className="text-xs text-muted-foreground">Billed for every night in this ward, like the bed — e.g. nursing, RMO, diet.</p>
      </div>
      {rows.map((r, i) => (
        // Phones: name on its own line, then type · amount · remove
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_5.5rem_2rem] sm:grid-cols-[minmax(0,1fr)_8.5rem_5.5rem_2rem] gap-1.5 items-center">
          <Input aria-label="Charge name" value={r.name} onChange={e => update(i, { name: e.target.value })} placeholder="Nursing charges" className="h-8 text-sm col-span-3 sm:col-span-1" />
          <select aria-label="Type" value={r.charge_type} onChange={e => update(i, { charge_type: e.target.value as DailyChargeType })}
            className="h-8 touch:h-10 w-full rounded-md border border-field bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
            {DAILY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Input aria-label="Amount per day (₹)" type="number" inputMode="decimal" min="0" value={r.amount} onChange={e => update(i, { amount: e.target.value })} placeholder="₹/day" className="h-8 text-sm font-mono" />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground" aria-label={`Remove ${r.name || 'charge'}`}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      {rows.length < 10 && (
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onChange([...rows, { name: '', charge_type: 'nursing', amount: '' }])}>
          <Plus className="w-3.5 h-3.5" /> Add daily charge
        </Button>
      )}
    </div>
  );
}

/** The rows as the API wants them, or an error to show */
function dailyPayload(rows: DailyRow[]): { charges?: { name: string; charge_type: DailyChargeType; amount_per_day: number }[]; error?: string } {
  const charges = [];
  for (const r of rows) {
    if (!r.name.trim() && !r.amount) continue;
    const amount = parseFloat(r.amount);
    if (!r.name.trim()) return { error: 'Give each daily charge a name' };
    if (!(amount > 0)) return { error: `Enter the amount per day for ${r.name.trim()}` };
    charges.push({ name: r.name.trim(), charge_type: r.charge_type, amount_per_day: amount });
  }
  return { charges };
}

// ─── Bed form ─────────────────────────────────────────────────────────────────

interface BedForm { bed_number: string; rent_per_day: string; notes: string; status: string; is_active: boolean; }
const emptyBed = (): BedForm => ({ bed_number: '', rent_per_day: '', notes: '', status: 'available', is_active: true });

// Generates sequential bed numbers from a starting number, e.g. "A-1" x3 -> ["A-1","A-2","A-3"]
function generateBedNumbers(start: string, count: number): string[] {
  const trimmed = start.trim();
  if (count <= 1) return [trimmed];
  const m = trimmed.match(/^(.*?)(\d+)$/);
  if (!m) return [trimmed];
  const [, prefix, numStr] = m;
  const width = numStr.length;
  const startNum = parseInt(numStr, 10);
  return Array.from({ length: count }, (_, i) =>
    prefix + String(startNum + i).padStart(width, '0')
  );
}

// ─── Shared select ────────────────────────────────────────────────────────────

// "icu" reads as ICU, not "Icu"; the rest are capitalised by CSS
const typeLabel = (t: string) => (t === 'icu' ? 'ICU' : t.replace('_', ' '));

function Sel({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: readonly string[] }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="h-8 touch:h-10 w-full rounded-md border border-field bg-background px-2 text-sm capitalize focus:outline-none focus:ring-1 focus:ring-ring">
      {options.map(o => <option key={o} value={o}>{typeLabel(o)}</option>)}
    </select>
  );
}

// ─── Bed row ──────────────────────────────────────────────────────────────────

function BedRow({ bed, wardId, onRefresh }: { bed: Bed; wardId: string; onRefresh: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<BedForm>({
    bed_number:  bed.bed_number,
    rent_per_day: bed.rent_per_day != null ? String(bed.rent_per_day) : '',
    notes:  bed.notes || '',
    status: bed.status,
    is_active: bed.is_active !== 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const occupiedByAdmission = !!bed.current_admission_id;

  const save = async () => {
    setError('');
    if (occupiedByAdmission && !form.is_active) {
      setError('Cannot deactivate a bed with a current admission');
      return;
    }
    setSaving(true);
    try {
      await updateBed(bed.id, {
        bed_number:  form.bed_number,
        rent_per_day: form.rent_per_day ? parseFloat(form.rent_per_day) : null,
        notes:  form.notes || null,
        status: form.status,
        is_active: form.is_active ? 1 : 0,
      });
      setOpen(false);
      onRefresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to save bed');
    } finally {
      setSaving(false);
    }
  };

  const statusTone: Record<string, StatusTone> = {
    available:   'success',
    occupied:    'accent',
    maintenance: 'warning',
    reserved:    'neutral',
  };

  return (
    <>
      <div className="flex items-center gap-3 min-h-10 pl-4 pr-2 py-1 border-b border-divider last:border-0">
        <BedIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-2">
          <span className={cn('text-sm font-medium', bed.is_active === 0 ? 'text-muted-foreground' : 'text-foreground')}>{bedLabel(bed.bed_number)}</span>
          {bed.current_patient_name && (
            <span className="text-sm text-muted-foreground truncate capitalize">{bed.current_patient_name}</span>
          )}
        </div>
        {/* Rent only when the bed overrides the ward default */}
        <span className="hidden sm:block w-24 text-right font-mono text-xs tabular-nums text-muted-foreground">
          {bed.rent_per_day != null ? `₹${Number(bed.rent_per_day).toLocaleString('en-IN')}/day` : ''}
        </span>
        <div className="flex items-center justify-end gap-1 sm:w-40 flex-shrink-0">
          {bed.is_active === 0 && <StatusChip tone="neutral">Inactive</StatusChip>}
          <StatusChip tone={statusTone[bed.occupancy_status] ?? 'neutral'} className="capitalize">{bed.occupancy_status}</StatusChip>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" onClick={() => setOpen(true)}
            aria-label={`Edit ${bedLabel(bed.bed_number)}`} title="Edit bed">
            <Pencil className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Bed</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Bed Number</label>
              <Input value={form.bed_number} onChange={e => setForm(f => ({ ...f, bed_number: e.target.value }))} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Rent/Day (₹) — blank = use ward default</label>
              <Input type="number" value={form.rent_per_day} onChange={e => setForm(f => ({ ...f, rent_per_day: e.target.value }))} className="h-8 text-sm" placeholder="Use ward default" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
              {occupiedByAdmission ? (
                <p className="text-xs text-muted-foreground py-1.5">
                  Occupied by {bed.current_patient_name || 'a current admission'} — status is managed automatically.
                </p>
              ) : (
                <Sel value={form.status} onChange={v => setForm(f => ({ ...f, status: v }))} options={BED_STATUSES} />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-xs font-medium">Active</label>
                <p className="text-xs text-muted-foreground">Inactive beds are hidden from admission &amp; bed-count totals.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                disabled={occupiedByAdmission}
              />
            </div>
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Ward card ────────────────────────────────────────────────────────────────

function WardCard({ ward, onRefresh }: { ward: Ward; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [loadingBeds, setLoadingBeds] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addBedOpen, setAddBedOpen] = useState(false);
  const [wardForm, setWardForm] = useState<WardForm>({
    name: ward.name, ward_type: ward.ward_type,
    default_rent_per_day: String(ward.default_rent_per_day), notes: ward.notes || '',
    is_active: ward.is_active !== 0,
  });
  const [daily, setDaily] = useState<DailyRow[]>(dailyRows(ward));
  const [bedForm, setBedForm] = useState<BedForm>(emptyBed());
  const [bedQuantity, setBedQuantity] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadBeds = useCallback(async () => {
    setLoadingBeds(true);
    const data = await getBeds(ward.id);
    setBeds(data ?? []);
    setLoadingBeds(false);
  }, [ward.id]);

  useEffect(() => { if (expanded) loadBeds(); }, [expanded, loadBeds]);

  const saveWard = async () => {
    setError('');
    const { charges, error: dailyError } = dailyPayload(daily);
    if (dailyError) { setError(dailyError); return; }
    setSaving(true);
    try {
      await updateWard(ward.id, {
        name: wardForm.name, ward_type: wardForm.ward_type,
        default_rent_per_day: parseFloat(wardForm.default_rent_per_day) || 0,
        notes: wardForm.notes || null, is_active: wardForm.is_active ? 1 : 0,
      });
      await setWardDailyCharges(ward.id, charges!);
      setEditOpen(false); onRefresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to save ward');
    } finally {
      setSaving(false);
    }
  };

  const saveBed = async () => {
    setError('');
    if (!bedForm.bed_number.trim()) { setError('Bed number is required'); return; }
    const count = Math.min(Math.max(parseInt(bedQuantity, 10) || 1, 1), 50);
    const bedNumbers = generateBedNumbers(bedForm.bed_number, count);
    setSaving(true);
    try {
      for (const bed_number of bedNumbers) {
        await createBed(ward.id, {
          bed_number,
          rent_per_day: bedForm.rent_per_day ? parseFloat(bedForm.rent_per_day) : null,
          notes: bedForm.notes || null,
        });
      }
      setAddBedOpen(false); setBedForm(emptyBed()); setBedQuantity('1'); loadBeds(); onRefresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to add bed(s)');
      loadBeds(); onRefresh();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Ward header — tap to show or hide its beds */}
      <div className="flex items-center gap-2 pl-4 pr-2 py-2">
        <button className="self-stretch flex flex-wrap items-center gap-x-4 gap-y-1 flex-1 min-w-0 text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
          <span className="flex items-baseline gap-2 min-w-0">
            <span className={cn('font-semibold text-sm truncate', ward.is_active === 0 ? 'text-muted-foreground' : 'text-foreground')}>{ward.name}</span>
            {/* Type only when the name doesn't already say it ("ICU", "General Ward") */}
            {!ward.name.toLowerCase().includes(typeLabel(ward.ward_type).toLowerCase()) && (
              <span className="text-xs text-muted-foreground capitalize whitespace-nowrap">{typeLabel(ward.ward_type)}</span>
            )}
            {ward.is_active === 0 && <StatusChip tone="neutral" className="self-center">Inactive</StatusChip>}
          </span>
          <span className="flex items-center gap-4 text-xs text-muted-foreground whitespace-nowrap">
            <span><span className="font-mono tabular-nums text-foreground">{ward.occupied_beds}/{ward.bed_count}</span> occupied</span>
            <span><span className="font-mono tabular-nums text-foreground">₹{Number(ward.default_rent_per_day).toLocaleString('en-IN')}</span>/day</span>
            {!!ward.daily_charges?.length && (
              <span title={ward.daily_charges.map(d => `${d.name} ₹${d.amount}`).join(', ')}>
                + <span className="font-mono tabular-nums text-foreground">₹{ward.daily_charges.reduce((n, d) => n + Number(d.amount), 0).toLocaleString('en-IN')}</span>/day ward charges
              </span>
            )}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 ml-auto text-muted-foreground" /> : <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground" />}
        </button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground flex-shrink-0" onClick={() => { setError(''); setDaily(dailyRows(ward)); setEditOpen(true); }}
          aria-label={`Edit ${ward.name}`} title="Edit ward">
          <Pencil className="w-4 h-4" />
        </Button>
      </div>

      {/* Beds */}
      {expanded && (
        <div className="border-t border-border">
          {loadingBeds ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">Loading beds…</div>
          ) : beds.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">No beds added yet.</div>
          ) : (
            beds.map(b => <BedRow key={b.id} bed={b} wardId={ward.id} onRefresh={loadBeds} />)
          )}
          <div className="px-4 py-2 border-t border-border bg-muted/30">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setBedForm(emptyBed()); setBedQuantity('1'); setError(''); setAddBedOpen(true); }}>
              <Plus className="w-4 h-4" /> Add bed
            </Button>
          </div>
        </div>
      )}

      {/* Edit ward dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Ward</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
              <Input value={wardForm.name} onChange={e => setWardForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Type</label>
              <Sel value={wardForm.ward_type} onChange={v => setWardForm(f => ({ ...f, ward_type: v }))} options={WARD_TYPES} /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Default Rent/Day (₹)</label>
              <Input type="number" value={wardForm.default_rent_per_day} onChange={e => setWardForm(f => ({ ...f, default_rent_per_day: e.target.value }))} className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={wardForm.notes} onChange={e => setWardForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" /></div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-xs font-medium">Active</label>
                <p className="text-xs text-muted-foreground">Inactive wards are hidden from new admissions.</p>
              </div>
              <Switch
                checked={wardForm.is_active}
                onCheckedChange={v => setWardForm(f => ({ ...f, is_active: v }))}
              />
            </div>
            <DailyChargesEditor rows={daily} onChange={setDaily} />
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveWard} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add bed dialog */}
      <Dialog open={addBedOpen} onOpenChange={setAddBedOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Bed — {ward.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  {parseInt(bedQuantity, 10) > 1 ? 'First Bed Number *' : 'Bed Number *'}
                </label>
                <Input value={bedForm.bed_number} onChange={e => setBedForm(f => ({ ...f, bed_number: e.target.value }))} placeholder="e.g. 101, A-1" className="h-8 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Quantity</label>
                <Input type="number" min="1" max="50" value={bedQuantity} onChange={e => setBedQuantity(e.target.value)} className="h-8 text-sm" />
              </div>
            </div>
            {parseInt(bedQuantity, 10) > 1 && (
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const count = Math.min(Math.max(parseInt(bedQuantity, 10) || 1, 1), 50);
                  const numbers = generateBedNumbers(bedForm.bed_number || '', count);
                  if (numbers.length <= 1) {
                    return 'Bed number must end in a digit to add multiple beds (e.g. "A-1").';
                  }
                  const preview = numbers.slice(0, 3).join(', ');
                  return `Will create ${numbers.length} beds: ${preview}${numbers.length > 3 ? ', …' : ''}`;
                })()}
              </p>
            )}
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Rent/Day (₹) — blank = ward default (₹{ward.default_rent_per_day})</label>
              <Input type="number" value={bedForm.rent_per_day} onChange={e => setBedForm(f => ({ ...f, rent_per_day: e.target.value }))} className="h-8 text-sm" placeholder="Use ward default" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={bedForm.notes} onChange={e => setBedForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" /></div>
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBedOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveBed} disabled={saving}>
              {saving ? 'Saving…' : parseInt(bedQuantity, 10) > 1 ? `Add ${Math.min(Math.max(parseInt(bedQuantity, 10) || 1, 1), 50)} Beds` : 'Add Bed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IPDSettingsPage() {
  usePageTitle('Wards & beds');
  const { currentUser } = useAuth();
  const { isOwner, hasFeature, can } = usePermission();
  const [wards, setWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(true);
  const [addWardOpen, setAddWardOpen] = useState(false);
  const [wardForm, setWardForm] = useState<WardForm>(emptyWard());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    const data = await getWards(currentUser.clinic_id);
    setWards(data ?? []);
    setLoading(false);
  }, [currentUser?.clinic_id]);

  useEffect(() => { load(); }, [load]);

  // Same rule as the server (requireWardManager): owner, or anyone allowed to manage users
  const canManageWards = isOwner || can('users:write');

  const saveWard = async () => {
    setError('');
    if (!wardForm.name.trim()) { setError('Ward name is required'); return; }
    setSaving(true);
    try {
      await createWard({
        name: wardForm.name, ward_type: wardForm.ward_type,
        default_rent_per_day: parseFloat(wardForm.default_rent_per_day) || 0,
        notes: wardForm.notes || null,
      });
      setAddWardOpen(false); setWardForm(emptyWard()); load();
    } catch (e: any) {
      setError(e?.message || 'Failed to create ward');
    } finally {
      setSaving(false);
    }
  };

  if (!hasFeature('ipd')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BedIcon className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">IPD Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          In-patient department management is not included in your current plan. Contact support to upgrade.
        </p>
      </div>
    );
  }

  if (!canManageWards) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <AlertTriangle className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">Access Restricted</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          Only the clinic owner or an admin can manage wards and beds.
        </p>
        <Link to="/ipd" className="text-sm text-primary mt-3 hover:underline">Back to IPD</Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/ipd" aria-label="Back to IPD" className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'h-8 w-8 p-0 -ml-2 flex-shrink-0')}>
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold leading-7 text-foreground">Wards &amp; beds</h1>
            {!loading && wards.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {wards.length} ward{wards.length !== 1 ? 's' : ''} ·{' '}
                <span className="tabular-nums">{wards.reduce((n, w) => n + Number(w.occupied_beds || 0), 0)}</span> of{' '}
                <span className="tabular-nums">{wards.reduce((n, w) => n + Number(w.bed_count || 0), 0)}</span> beds occupied
              </p>
            )}
          </div>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => { setWardForm(emptyWard()); setError(''); setAddWardOpen(true); }}>
          <Plus className="w-4 h-4" /> New ward
        </Button>
      </div>

      <div className="flex-1 overflow-auto">
      <div className="max-w-4xl px-4 sm:px-6 py-4 space-y-3">
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
        ) : wards.length === 0 ? (
          <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
            <BedIcon className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-semibold text-foreground">No wards yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add your first ward, then its beds.</p>
          </div>
        ) : (
          wards.map(w => <WardCard key={w.id} ward={w} onRefresh={load} />)
        )}
      </div>
      </div>

      <Dialog open={addWardOpen} onOpenChange={setAddWardOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New Ward</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Name *</label>
              <Input value={wardForm.name} onChange={e => setWardForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. General Ward, ICU" className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Type</label>
              <Sel value={wardForm.ward_type} onChange={v => setWardForm(f => ({ ...f, ward_type: v }))} options={WARD_TYPES} /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Default Rent/Day (₹)</label>
              <Input type="number" value={wardForm.default_rent_per_day} onChange={e => setWardForm(f => ({ ...f, default_rent_per_day: e.target.value }))} className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={wardForm.notes} onChange={e => setWardForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" /></div>
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddWardOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveWard} disabled={saving}>{saving ? 'Saving…' : 'Create Ward'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
